/**
 * Status Toggle Component
 * Toggle switch for Active/Inactive status with visual feedback
 */

import React from 'react';
import clsx from 'clsx';

export interface StatusToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  activeLabel?: string;
  inactiveLabel?: string;
  size?: 'sm' | 'md' | 'lg';
}

const StatusToggle: React.FC<StatusToggleProps> = ({
  checked,
  onChange,
  disabled = false,
  activeLabel = 'Active',
  inactiveLabel = 'Inactive',
  size = 'md',
}) => {
  const sizeStyles = {
    sm: {
      switch: 'w-10 h-5',
      toggle: 'w-4 h-4',
      translate: 'translate-x-5',
      text: 'text-xs',
    },
    md: {
      switch: 'w-12 h-6',
      toggle: 'w-5 h-5',
      translate: 'translate-x-6',
      text: 'text-sm',
    },
    lg: {
      switch: 'w-14 h-7',
      toggle: 'w-6 h-6',
      translate: 'translate-x-7',
      text: 'text-base',
    },
  };

  const styles = sizeStyles[size];

  return (
    <div className="flex items-center space-x-3">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={clsx(
          'relative inline-flex flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2',
          styles.switch,
          checked
            ? 'bg-green-600 focus:ring-green-500'
            : 'bg-red-600 focus:ring-red-500',
          disabled && 'opacity-50 cursor-not-allowed',
          !disabled && 'cursor-pointer'
        )}
      >
        <span
          aria-hidden="true"
          className={clsx(
            'pointer-events-none inline-block rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out',
            styles.toggle,
            checked ? styles.translate : 'translate-x-0'
          )}
        />
      </button>
      <span
        className={clsx(
          'font-medium',
          styles.text,
          checked ? 'text-green-700' : 'text-red-700',
          disabled && 'opacity-50'
        )}
      >
        {checked ? activeLabel : inactiveLabel}
      </span>
    </div>
  );
};

export default StatusToggle;
